import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../environments/environment';

export type AccessAreaId = 'kenya' | 'south-africa' | 'insights';

export interface AuthorizedOrganisation {
  id: number;
  name: string;
  domain: string;
}

interface PortalUser {
  email?: string;
  roles?: unknown;
  assigned_orgs?: unknown[];
  [key: string]: unknown;
}

interface LoginResponse {
  logintoken?: string[];
  token?: string;
  user?: PortalUser;
  roles?: unknown;
  assigned_orgs?: unknown[];
}

interface ProfileResponse extends PortalUser {
  user?: PortalUser;
  data?: PortalUser;
}

export interface CentralSession {
  token: string;
  email: string;
  user: PortalUser;
  roles: string[];
  organisationNames: string[];
  organisations: AuthorizedOrganisation[];
  accessAreas: AccessAreaId[];
}

@Injectable({providedIn: 'root'})
export class CentralAuthService {
  private readonly sessionKey = 'hivPdsCentralSession';

  constructor(private http: HttpClient) {}

  get session(): CentralSession | null {
    try {
      const value = localStorage.getItem(this.sessionKey);
      return value ? JSON.parse(value) as CentralSession : null;
    } catch {
      return null;
    }
  }

  async emailLogin(email: string, password: string): Promise<CentralSession> {
    const response = await firstValueFrom(
      this.http.post<LoginResponse>(`${environment.cmsApiUrl}/enterprise/userLogin`, {email, password})
    );
    return this.createSession(response, email);
  }

  async googleLogin(credential: string): Promise<CentralSession> {
    const email = this.emailFromGoogleCredential(credential);
    const response = await firstValueFrom(
      this.http.post<LoginResponse>(`${environment.cmsApiUrl}/inspiniaGmailLogin`, {email})
    );
    return this.createSession(response, email);
  }

  async restoreSession(): Promise<CentralSession | null> {
    const current = this.session;
    if (!current?.token) return null;
    try {
      const response = await firstValueFrom(this.http.get<ProfileResponse>(`${environment.cmsApiUrl}/profile`, {
        headers: this.authHeaders(current.token)
      }));
      const profile = response.user || response.data || response;
      const roleSource = response.roles ?? profile.roles;
      const assignmentSource = response.assigned_orgs ?? profile.assigned_orgs;
      const roles = roleSource === undefined ? current.roles : this.normalizeRoles(roleSource);
      const assignedOrgs = assignmentSource === undefined
        ? current.user.assigned_orgs || []
        : assignmentSource;
      const user: PortalUser = {
        ...current.user,
        ...profile,
        roles,
        assigned_orgs: assignedOrgs
      };
      const assignedNames = this.organisationNames(assignedOrgs);
      const organisationNames = this.isAdministrator(roles)
        ? current.organisations.map(organisation => organisation.name)
        : assignedNames;
      const updated: CentralSession = {
        ...current,
        email: profile.email || current.email,
        user,
        roles,
        organisationNames,
        accessAreas: this.resolveAccessAreas(roles, organisationNames)
      };
      this.persist(updated);
      return updated;
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        this.logout();
        return null;
      }
      // A transient network or 5xx error must not destroy a valid local session.
      return current;
    }
  }

  async refreshEntitlements(): Promise<CentralSession> {
    const current = this.session;
    if (!current) throw new Error('No active session.');
    const roles = this.normalizeRoles(current.roles);
    let organisations = this.organisations(current.user.assigned_orgs);
    try {
      const response = await firstValueFrom(
        this.http.get<{data?: unknown[]}>(`${environment.cmsApiUrl}/enterprise/organisations?per_page=1000`, {
          headers: this.authHeaders(current.token)
        })
      );
      const fetched = this.organisations(response.data || []);
      if (fetched.length) organisations = fetched;
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) throw error;
      // Assigned organisations from login remain a valid fallback.
    }
    const assignedNames = this.organisationNames(current.user.assigned_orgs);
    const assignedIds = this.organisationIds(current.user.assigned_orgs);
    const assignedOrganisations = assignedIds.size
      ? organisations.filter(org => assignedIds.has(org.id))
      : [];
    const organisationNames = this.isAdministrator(roles)
      ? organisations.map(org => org.name)
      : assignedOrganisations.length
        ? assignedOrganisations.map(org => org.name)
        : assignedNames;
    const updated = {
      ...current,
      roles,
      organisationNames,
      organisations,
      accessAreas: this.resolveAccessAreas(roles, organisationNames)
    };
    this.persist(updated);
    return updated;
  }

  logout(): void {
    localStorage.removeItem(this.sessionKey);
  }

  assignedCountry(session: CentralSession | null = this.session): 'Kenya' | 'South Africa' | null {
    if (!session) return null;
    const assignedIds = this.organisationIds(session.user.assigned_orgs || []);
    const names = [
      ...this.organisationNames(session.user.assigned_orgs || []),
      ...session.organisations
        .filter(organisation => assignedIds.has(organisation.id))
        .map(organisation => organisation.name)
    ];
    const kenyaOrganisationIds = [56, 60, 63, 70];
    const southAfricaOrganisationIds = [86, 87, 88, 100];
    const belongsToKenya = names.some(name => {
      const normalized = name.toLowerCase();
      return normalized.includes('kenya') ||
        (normalized.includes('hiv pds') && !normalized.includes('south africa'));
    }) || kenyaOrganisationIds.some(id => assignedIds.has(id));
    if (belongsToKenya) return 'Kenya';
    const belongsToSouthAfrica = names.some(name =>
      name.toLowerCase().includes('south africa')
    ) || southAfricaOrganisationIds.some(id => assignedIds.has(id));
    if (belongsToSouthAfrica) return 'South Africa';
    return null;
  }

  findOrganisation(
    patterns: string[], exclusions: string[] = [], organisationIds: number[] = [],
    countryAccess?: 'Kenya' | 'South Africa'
  ): AuthorizedOrganisation | null {
    const organisations = this.session?.organisations || [];
    const matchesName = (nameValue: string) => {
      const name = nameValue.toLowerCase();
      return patterns.some(pattern => name.includes(pattern.toLowerCase())) &&
        !exclusions.some(exclusion => name.includes(exclusion.toLowerCase()));
    };
    const matches = (organisation: AuthorizedOrganisation) => organisationIds.length
      ? organisationIds.includes(organisation.id)
      : matchesName(organisation.name);
    const assignedIds = this.organisationIds(this.session?.user.assigned_orgs || []);
    const assignedById = organisations.find(org =>
      assignedIds.has(org.id) && matches(org)
    );
    if (assignedById) return assignedById;

    if (!organisationIds.length) {
      const assignedMatches = this.organisationNames(this.session?.user.assigned_orgs || [])
        .filter(matchesName)
        .map(name => name.toLowerCase());
      const assignedOrganisation = organisations.find(org =>
        assignedMatches.includes(org.name.toLowerCase())
      );
      if (assignedOrganisation) return assignedOrganisation;
    }

    if (countryAccess && this.assignedCountry() === countryAccess) {
      const countryOrganisation = organisations.find(matches);
      if (countryOrganisation) return countryOrganisation;
    }

    const roles = this.session?.roles || [];
    if (!this.isAdministrator(roles)) return null;

    const candidates = organisations.filter(matches);
    const assignedCountryNames = this.session?.organisationNames || [];
    const hasSouthAfrica = assignedCountryNames.some(name => name.toLowerCase().includes('south africa'));
    const hasKenya = assignedCountryNames.some(name => {
      const normalized = name.toLowerCase();
      return normalized.includes('kenya') ||
        (normalized.includes('hiv pds') && !normalized.includes('south africa'));
    });
    if (hasSouthAfrica && !hasKenya) {
      return candidates.find(org => org.name.toLowerCase().includes('south africa')) || candidates[0] || null;
    }
    if (hasKenya && !hasSouthAfrica) {
      return candidates.find(org => !org.name.toLowerCase().includes('south africa')) || candidates[0] || null;
    }
    return candidates[0] || null;
  }

  async openOrganisation(organisation: AuthorizedOrganisation, configuredUrl?: string): Promise<void> {
    const current = this.session;
    if (!current) throw new Error('Your session has expired. Please sign in again.');
    const destination = configuredUrl
      ? this.validDestination(configuredUrl)
      : this.organisationDestination(organisation);
    if (!destination) throw new Error(`${organisation.name} does not have a valid staging destination in CMS.`);
    const response = await firstValueFrom(this.http.post<{token?: string}>(
      `${environment.cmsApiUrl}/enterprise/generate-token`,
      {enterprise_org_id: String(organisation.id)},
      {headers: this.authHeaders(current.token)}
    ));
    if (!response.token) throw new Error('The destination access token could not be generated.');

    const query = new URLSearchParams({
      token: response.token,
      enterprise_org_id: String(organisation.id),
      email: current.email
    }).toString();
    destination.pathname = `${destination.pathname.replace(/\/$/, '')}/verify.html`;
    destination.search = query;
    window.open(destination.toString(), '_blank', 'noopener');
  }

  openDirectUrl(configuredUrl: string): void {
    try {
      const destination = new URL(configuredUrl);
      if (destination.protocol !== 'https:') throw new Error();
      window.open(destination.toString(), '_blank', 'noopener');
    } catch {
      throw new Error('The direct application URL is not valid.');
    }
  }

  isLaunchableOrganisation(organisation: AuthorizedOrganisation): boolean {
    return !!this.organisationDestination(organisation);
  }

  isLaunchableUrl(url: string): boolean {
    return !!this.validDestination(url);
  }

  private organisationDestination(organisation: AuthorizedOrganisation): URL | null {
    return this.validDestination(organisation.domain);
  }

  private validDestination(value: string): URL | null {
    const domain = value.trim().replace(/^["']|["']$/g, '');
    try {
      const destination = new URL(/^https?:\/\//i.test(domain) ? domain : `https://${domain}`);
      const hostname = destination.hostname.toLowerCase();
      const stagingRequired = environment.cmsApiUrl.includes('//staging.');
      if (
        destination.protocol !== 'https:' ||
        ['localhost', '127.0.0.1', '::1'].includes(hostname) ||
        (stagingRequired && !hostname.startsWith('staging.'))
      ) return null;
      return destination;
    } catch {
      return null;
    }
  }

  private createSession(response: LoginResponse, fallbackEmail: string): CentralSession {
    const token = response.logintoken?.[0] || response.token;
    if (!token || !response.user) {
      throw new Error('The login response did not include a user and access token.');
    }

    const roles = this.normalizeRoles(response.roles ?? response.user.roles);
    const assignedOrgs = response.assigned_orgs ?? response.user.assigned_orgs ?? [];
    const organisations = this.organisations(assignedOrgs);
    const organisationNames = this.organisationNames(assignedOrgs);
    const user: PortalUser = {...response.user, roles, assigned_orgs: assignedOrgs};
    const session: CentralSession = {
      token,
      email: response.user.email || fallbackEmail,
      user,
      roles,
      organisationNames,
      organisations,
      accessAreas: this.resolveAccessAreas(roles, organisationNames)
    };
    this.persist(session);
    return session;
  }

  private resolveAccessAreas(roles: string[], organisationNames: string[]): AccessAreaId[] {
    if (this.isAdministrator(roles)) return ['kenya', 'south-africa', 'insights'];

    const names = organisationNames.join(' ').toLowerCase();
    const granted = new Set<AccessAreaId>();
    const kenyaRoles = [
      'health_worker', 'coordinator', 'boh', 'isinya', 'kitengela',
      'partner', 'sub_county', 'county',
      'fulfillment', 'business_user', 'cxo_business_user', 'business_partners', 'hiv_pds'
    ];
    const southAfricaRoles = [
      'sa_health_worker', 'health_worker', 'coordinator', 'sa_fulfillment',
      'fulfillment', 'partner', 'county', 'sub_county', 'business_user',
      'cxo_business_user', 'business_partners'
    ];
    const insightRoles = [
      'boh', 'isinya', 'kitengela', 'business_partners', 'cxo_business_user',
      'coordinator', 'county', 'sub_county'
    ];

    const hasKenyaOrganisation = organisationNames.some(name => {
      const normalized = name.toLowerCase();
      return normalized.includes('kenya') ||
        (normalized.includes('hiv pds') && !normalized.includes('south africa'));
    });

    const hasSouthAfricaOrganisation = names.includes('south africa');
    if (hasKenyaOrganisation || roles.some(role => kenyaRoles.includes(role))) {
      granted.add('kenya');
    }

    if (
      roles.some(role => southAfricaRoles.includes(role)) ||
      hasSouthAfricaOrganisation
    ) granted.add('south-africa');

    if (
      roles.some(role => insightRoles.includes(role)) ||
      /insights|predictive analytics/.test(names)
    ) granted.add('insights');

    return [...granted];
  }

  private normalizeRoles(value: unknown): string[] {
    let roles: unknown[] = [];
    if (typeof value === 'string') roles = value.split(',');
    else if (Array.isArray(value)) roles = value;
    else if (value && typeof value === 'object') roles = Object.values(value);
    return roles
      .flatMap(role => {
        if (typeof role === 'string') return [role];
        if (role && typeof role === 'object') {
          const candidate = role as Record<string, unknown>;
          return [
            candidate['slug'] ??
            candidate['role_slug'] ??
            candidate['name'] ??
            candidate['role'] ??
            candidate['type'] ??
            ''
          ];
        }
        return [];
      })
      .map(role => String(role).trim().toLowerCase())
      .map(role => role.replace(/[\s-]+/g, '_'))
      .map(role => ({
        facility_health_worker: 'health_worker',
        facility_coordinator: 'coordinator',
        kasha_fulfillment: 'fulfillment',
        partner_investor: 'partner',
        county_facility_coordinator: 'county',
        sub_county_facility_coordinator: 'sub_county'
      }[role] || role))
      .filter(Boolean);
  }

  private organisationIds(value: unknown): Set<number> {
    if (!Array.isArray(value)) return new Set<number>();
    return new Set(value.flatMap(item => {
      if (typeof item === 'number' || (typeof item === 'string' && /^\d+$/.test(item.trim()))) {
        const id = Number(item);
        return Number.isFinite(id) ? [id] : [];
      }
      if (!item || typeof item !== 'object') return [];
      const record = item as Record<string, unknown>;
      const nested = record['organisation'];
      const source = nested && typeof nested === 'object'
        ? nested as Record<string, unknown>
        : record;
      const pivot = record['pivot'];
      const assignment = record['assignment'];
      const relation = pivot && typeof pivot === 'object'
        ? pivot as Record<string, unknown>
        : assignment && typeof assignment === 'object'
          ? assignment as Record<string, unknown>
          : {};
      const id = Number(
        source['id'] ?? record['org_id'] ?? record['organisation_id'] ??
        record['enterprise_org_id'] ?? relation['org_id'] ??
        relation['organisation_id'] ?? relation['enterprise_org_id']
      );
      return Number.isFinite(id) ? [id] : [];
    }));
  }

  private organisationNames(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value
      .map(item => {
        if (typeof item === 'string') return item.trim();
        if (!item || typeof item !== 'object') return '';
        const record = item as Record<string, unknown>;
        const nested = record['organisation'];
        const source = nested && typeof nested === 'object'
          ? nested as Record<string, unknown>
          : record;
        return String(source['name'] || '').trim();
      })
      .filter(Boolean);
  }

  private organisations(value: unknown): AuthorizedOrganisation[] {
    if (!Array.isArray(value)) return [];
    return value
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const record = item as Record<string, unknown>;
        const nested = record['organisation'];
        const source = nested && typeof nested === 'object'
          ? nested as Record<string, unknown>
          : record;
        const id = Number(
          source['id'] ?? record['org_id'] ?? record['organisation_id'] ?? record['enterprise_org_id']
        );
        const name = String(source['name'] || '').trim();
        const domain = String(source['domain'] || '').trim();
        return Number.isFinite(id) && name && domain ? {id, name, domain} : null;
      })
      .filter((org): org is AuthorizedOrganisation => !!org);
  }

  private isAdministrator(roles: string[]): boolean {
    return roles.some(role => ['super_admin', 'admin_eng', 'administrator'].includes(role));
  }

  private emailFromGoogleCredential(token: string): string {
    try {
      const payload = token.split('.')[1];
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = decodeURIComponent(
        atob(normalized).split('').map(char =>
          `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`
        ).join('')
      );
      const data = JSON.parse(decoded) as {email?: string; exp?: number};
      if (!data.email || (data.exp && data.exp < Math.floor(Date.now() / 1000))) {
        throw new Error();
      }
      return data.email;
    } catch {
      throw new Error('The Google sign-in credential is invalid or expired.');
    }
  }

  private authHeaders(token: string): HttpHeaders {
    return new HttpHeaders({Authorization: `Bearer ${token}`, Accept: 'application/json'});
  }

  private persist(session: CentralSession): void {
    localStorage.setItem(this.sessionKey, JSON.stringify(session));
  }
}
